import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getConfiguration } from '../../../../../services/configurationManager';
import {
  getEffectiveHeartRateData,
  parseHeartRateData,
} from '../../../shared/hr-data';
import { FETCH_CONCURRENCY } from '../constants';
import type {
  DeterministicAnalysisResult,
  SessionRepairListRow,
  SessionRepairLiteSession,
} from '../types';
import { deriveSessionRepairStatus } from '../utils/deriveSessionRepairStatus';
import { buildScanStateFromConfiguration } from '../utils/deriveRepairStatusFromConfiguration';
import { getCachedConfiguration } from '../utils/getCachedConfiguration';
import { runDeterministicAnalysis } from '../utils/runDeterministicAnalysis';

type ScanState = {
  analysis?: DeterministicAnalysisResult;
  isAnalyzing: boolean;
  analysisFailed: boolean;
  eventCount?: number;
};

const shouldRescanSession = (
  session: SessionRepairLiteSession,
  existing?: ScanState
) => {
  if (!session.hasHrData || existing?.isAnalyzing) {
    return false;
  }

  const needsAnalysis = !session.hasHrDataRepaired;

  if (session.hasHrDataRepaired) {
    return Boolean(existing?.analysis) || existing?.eventCount == null;
  }

  return !(
    existing?.eventCount != null &&
    (!needsAnalysis || existing.analysis)
  );
};

export const useSessionRepairList = (sessions: SessionRepairLiteSession[]) => {
  const queryClient = useQueryClient();
  const [scanBySessionId, setScanBySessionId] = useState<
    Record<number, ScanState>
  >({});
  const queueRef = useRef<number[]>([]);
  const activeCountRef = useRef(0);
  const scanRef = useRef(scanBySessionId);
  const sessionsRef = useRef(sessions);

  scanRef.current = scanBySessionId;
  sessionsRef.current = sessions;

  const scanSession = useCallback(async (sessionId: number) => {
    const lite = sessionsRef.current.find(session => session.id === sessionId);
    const needsAnalysis = Boolean(lite?.hasHrData) && !lite?.hasHrDataRepaired;

    setScanBySessionId(current => ({
      ...current,
      [sessionId]: {
        ...current[sessionId],
        isAnalyzing: true,
        analysisFailed: false,
      },
    }));

    try {
      const response = await getConfiguration(sessionId);
      const events = parseHeartRateData(response.data.hrData);
      const effectiveEvents = getEffectiveHeartRateData(response.data);
      const analysis = needsAnalysis
        ? runDeterministicAnalysis(events)
        : undefined;

      setScanBySessionId(current => ({
        ...current,
        [sessionId]: {
          analysis,
          isAnalyzing: false,
          analysisFailed: false,
          eventCount: effectiveEvents.length,
        },
      }));
    } catch {
      setScanBySessionId(current => ({
        ...current,
        [sessionId]: {
          ...current[sessionId],
          isAnalyzing: false,
          analysisFailed: true,
        },
      }));
    }
  }, []);

  const processQueue = useCallback(() => {
    while (
      activeCountRef.current < FETCH_CONCURRENCY &&
      queueRef.current.length > 0
    ) {
      const sessionId = queueRef.current.shift();
      if (sessionId == null) break;

      const existing = scanRef.current[sessionId];
      const lite = sessionsRef.current.find(
        session => session.id === sessionId
      );

      if (existing?.isAnalyzing || !lite) continue;
      if (!shouldRescanSession(lite, existing)) {
        continue;
      }

      activeCountRef.current += 1;
      void scanSession(sessionId).finally(() => {
        activeCountRef.current -= 1;
        processQueue();
      });
    }
  }, [scanSession]);

  useEffect(() => {
    setScanBySessionId(current => {
      const next = { ...current };

      sessions.forEach(session => {
        if (!session.hasHrData) {
          return;
        }

        const existing = current[session.id];

        if (existing?.isAnalyzing) {
          return;
        }

        if (session.hasHrDataRepaired && existing?.analysis) {
          const cached = getCachedConfiguration(queryClient, session.id);
          if (cached) {
            next[session.id] = buildScanStateFromConfiguration(cached, session);
          }
          return;
        }

        if (!shouldRescanSession(session, existing)) {
          return;
        }

        const cached = getCachedConfiguration(queryClient, session.id);
        if (!cached) {
          return;
        }

        next[session.id] = buildScanStateFromConfiguration(cached, session);
      });

      return next;
    });

    const toScan = sessions
      .filter(session => session.hasHrData)
      .map(session => session.id);

    queueRef.current.push(
      ...toScan.filter(sessionId => {
        const existing = scanRef.current[sessionId];
        const lite = sessions.find(session => session.id === sessionId);

        if (!lite) {
          return false;
        }

        return shouldRescanSession(lite, existing);
      })
    );
    processQueue();
  }, [sessions, processQueue, queryClient]);

  const rows = useMemo<SessionRepairListRow[]>(
    () =>
      sessions.map(session => {
        const scan = scanBySessionId[session.id];
        const repairStatus = deriveSessionRepairStatus({
          hasHrData: session.hasHrData,
          hasHrDataRepaired: session.hasHrDataRepaired,
          analysis: scan?.analysis,
          isAnalyzing: scan?.isAnalyzing,
          analysisFailed: scan?.analysisFailed,
        });

        return {
          sessionId: session.id,
          patientCode: session.patientCode,
          sessionDate:
            session.started_at ?? session.created_at ?? session.updated_at,
          scenario: session.scenario,
          hasHrData: session.hasHrData,
          hasHrDataRepaired: session.hasHrDataRepaired,
          repairStatus,
          duplicateCount: scan?.analysis?.duplicateCount,
          gapCount: scan?.analysis?.gapCount,
          analysisFindings: scan?.analysis?.findings,
          eventCount: scan?.eventCount,
        };
      }),
    [scanBySessionId, sessions]
  );

  const statusCounts = useMemo(
    () =>
      rows.reduce(
        (counts, row) => {
          counts[row.repairStatus] = (counts[row.repairStatus] ?? 0) + 1;
          return counts;
        },
        {} as Record<string, number>
      ),
    [rows]
  );

  return { rows, statusCounts };
};
